import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError, type ErrorCode } from '@/api/errors';
import {
  APP_KEYS_0390,
  anyHasKey,
  hasKey,
  type AppKey0390,
  type AppKey0397,
} from '@/api/featureDetect';
import { isDeployPartial, isDeployProblem } from '@/api/routingDeploy';
import type {
  ApexPlan,
  AppRole,
  AppTopology,
  ProductAppDto,
  RoutingDeployOutcome,
  SetPrimaryResult,
  SiteType,
} from '@/api/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorBanner } from '@/components/ErrorBanner';
import { RedirectTargetNotice } from '@/components/RedirectTargetNotice';
import { TenantOverridesNotice } from '@/components/TenantOverridesNotice';
import { RoutingDeployBanner } from '@/components/RoutingDeployBanner';
import { deployProblemText } from '@/components/routingDeployText';
import { useSnackbar } from '@/components/Snackbar';
import { useCopy } from '@/hooks/useCopy';
import { ApexPlanView } from './ApexPlanView';

const DERIVED_ROLE: Record<AppRole, { kind: string; scope: string }> = {
  PUBLIC: { kind: 'Front-end', scope: 'Tenant-scoped' },
  TENANT_ADMIN: { kind: 'Front-end', scope: 'Tenant-scoped' },
  PRODUCT_ADMIN: { kind: 'Front-end', scope: 'Product-scoped' },
  SELF_CONTAINED: { kind: 'Self-contained', scope: 'Tenant-scoped' },
  API: { kind: 'Back-end', scope: 'Product-scoped' },
  RESELLER_ADMIN: { kind: 'Front-end', scope: 'Product-scoped' },
  AFFILIATE: { kind: 'Front-end', scope: 'Tenant-scoped' },
};

// SASADM-0152 — the codes AppDialog shows inline at a field; any other code a caller
// gives role-specific copy for (`errorCopy`) goes to the snackbar instead.
const DOCROOT_ERROR_CODES: readonly ErrorCode[] = [
  'INVALID_APP_DOCROOT',
  'FRONTEND_APP_HAS_UPSTREAM',
];
const UPSTREAM_ERROR_CODES: readonly ErrorCode[] = [
  'UPSTREAM_NOT_ALLOWED',
  'UPSTREAM_REQUIRED',
  'INVALID_UPSTREAM',
  'BACKEND_APP_HAS_DOCROOT',
  'SELF_CONTAINED_APP_HAS_DOCROOT',
];
const SLUG_ERROR_CODES: readonly ErrorCode[] = ['APP_SLUG_TAKEN', 'RESERVED_APP_SLUG'];

function isFieldErrorCode(code: ErrorCode): boolean {
  return (
    DOCROOT_ERROR_CODES.includes(code) ||
    UPSTREAM_ERROR_CODES.includes(code) ||
    SLUG_ERROR_CODES.includes(code)
  );
}

// SASADM-0176 — the derived scope (SASATH-0393 §1 covers tenant-scope app deletes only).
function isTenantScoped(app: ProductAppDto): boolean {
  return DERIVED_ROLE[app.role].scope === 'Tenant-scoped';
}

// SASADM-0133 — API and SELF_CONTAINED are reached through an `upstream` and
// never carry a docroot; the other roles are served from one.
function roleUsesUpstream(role: AppRole): boolean {
  return role === 'API' || role === 'SELF_CONTAINED';
}

// SASADM-0133 — a SELF_CONTAINED app is the product's only app. Primary-eligible
// roles can anchor the tenant apex.
function roleCanBePrimary(role: AppRole): boolean {
  return role === 'PUBLIC' || role === 'SELF_CONTAINED';
}

// SASADM-0172 (SASATH-0367) — the roles an app may be re-tagged to: a non-primary PUBLIC app to
// Tenant-Admin and/or Affiliate (each while that singleton is free), Tenant-Admin back to PUBLIC.
// Nothing ever changes away from AFFILIATE.
function roleChangeTargets(
  app: ProductAppDto | null,
  tenantAdminExists: boolean,
  affiliateExists: boolean,
): AppRole[] {
  if (app?.role === 'TENANT_ADMIN') return ['PUBLIC'];
  if (app?.role !== 'PUBLIC' || app.isPrimary) return [];
  const targets: AppRole[] = [];
  if (!tenantAdminExists) targets.push('TENANT_ADMIN');
  if (!affiliateExists) targets.push('AFFILIATE');
  return targets;
}

export function ProductApps({
  productId,
  productSlug,
  appTopology,
  appRole,
}: {
  productId: number;
  productSlug: string;
  appTopology: AppTopology;
  /** SASADM-0151 — one role panel per Apps sub-tab. Named appRole (not role) so JSX is not an ARIA role. */
  appRole: AppRole;
}) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [rows, setRows] = useState<ProductAppDto[] | null>(null);
  const [dialogRole, setDialogRole] = useState<AppRole | null>(null);
  const [editApp, setEditApp] = useState<ProductAppDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ProductAppDto | null>(null);
  const [retagPromptOpen, setRetagPromptOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  // SASADM-0170 (SASATH-0387) — an app create/update whose routing deploy was REJECTED / FAILED.
  const [deployFailure, setDeployFailure] = useState<RoutingDeployOutcome | null>(null);
  // SASADM-0176 (SASATH-0393 §1) — a refused app delete (APP_HAS_HOSTNAMES, APP_IS_PRIMARY,
  // HOSTNAME_IS_REDIRECT_TARGET), shown above the apps until closed. SASADM-0177 adds
  // PRIMARY_HOSTNAME_DELETE_BLOCKED and keeps the refused app for APP_IS_PRIMARY's Clear primary.
  const [deleteRefusal, setDeleteRefusal] = useState<{ err: ApiError; app: ProductAppDto } | null>(
    null,
  );
  // SASADM-0177 (SASATH-0394 §1) — the PUBLIC app awaiting Make primary's confirm.
  const [confirmPrimary, setConfirmPrimary] = useState<ProductAppDto | null>(null);
  // SASADM-0178 (SASATH-0395 §2) — a dry run's plan awaiting Make / Re-apply primary's confirm.
  const [primaryPreview, setPrimaryPreview] = useState<{
    app: ProductAppDto;
    plan: ApexPlan;
    reapply: boolean;
    fromApp: string | undefined;
  } | null>(null);
  // SASADM-0178 — what a real set-primary moved; cleared on the next action or a refresh.
  const [apexResult, setApexResult] = useState<{
    plan: ApexPlan;
    appName: string;
    fromApp: string | undefined;
  } | null>(null);
  const [apexResultOpen, setApexResultOpen] = useState(false);

  async function refresh() {
    setApexResult(null);
    try {
      setRows(await apiAdmin.productApps.list(productId));
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
      setRows([]);
    }
  }

  useEffect(() => {
    void (async () => {
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const publicApps = rows?.filter((app) => app.role === 'PUBLIC') ?? [];
  const tenantAdmin = rows?.find((app) => app.role === 'TENANT_ADMIN') ?? null;
  const productAdmin = rows?.find((app) => app.role === 'PRODUCT_ADMIN') ?? null;
  const selfContained = rows?.find((app) => app.role === 'SELF_CONTAINED') ?? null;
  const api = rows?.find((app) => app.role === 'API') ?? null;
  const resellerAdmin = rows?.find((app) => app.role === 'RESELLER_ADMIN') ?? null;
  const affiliate = rows?.find((app) => app.role === 'AFFILIATE') ?? null;
  // PRODUCT_ADMIN and RESELLER_ADMIN have their own sub-tabs (ProductAdmins /
  // ProductResellerAdmin); no ProductApps role panel renders them.
  const appsByRole: Record<AppRole, ProductAppDto[]> = {
    PUBLIC: publicApps,
    TENANT_ADMIN: tenantAdmin ? [tenantAdmin] : [],
    PRODUCT_ADMIN: productAdmin ? [productAdmin] : [],
    SELF_CONTAINED: selfContained ? [selfContained] : [],
    API: api ? [api] : [],
    RESELLER_ADMIN: resellerAdmin ? [resellerAdmin] : [],
    AFFILIATE: affiliate ? [affiliate] : [],
  };
  const hasSelfContained = Boolean(selfContained);
  const hasAnyApp = (rows?.length ?? 0) > 0;
  // SASADM-0178 (SASATH-0395) — binding: dry-run only when the app read carries the marker.
  const canPreview = anyHasKey(rows, 'setPrimaryDryRun');
  // SASADM-0134 — packaging-driven eligibility, layered ON TOP of 0133's
  // cardinality/exclusivity gate above (independent of existing app count).
  const topologyAllowsRole = (r: AppRole): boolean =>
    appTopology === 'SELF_CONTAINED' ? r === 'SELF_CONTAINED' : r !== 'SELF_CONTAINED';
  // Still fetch the full list (exclusivity / tenant-admin logic needs every row); only this
  // sub-tab's role section is rendered (SASADM-0151).

  function openAdd(role: AppRole) {
    setEditApp(null);
    setDialogRole(role);
  }

  function openEdit(app: ProductAppDto) {
    setEditApp(app);
    setDialogRole(app.role);
  }

  function closeEditor() {
    setDialogRole(null);
    setEditApp(null);
  }

  function requestDelete(app: ProductAppDto) {
    // SASADM-0176 (SASATH-0393 §1) — a TENANT-scope app's platform hostnames go with it; the
    // server refuses (APP_HAS_HOSTNAMES, listed) only on operator-added ones, so it decides.
    if (app.hasHostnames && !isTenantScoped(app)) {
      snack.error(
        t(
          'errors.codes.APP_HAS_HOSTNAMES',
          'This app still has hostnames assigned; reassign its hostnames first.',
        ),
      );
      return;
    }
    setConfirmDelete(app);
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setBusy(true);
    setDeleteRefusal(null);
    setApexResult(null);
    try {
      const result = await apiAdmin.productApps.delete(productId, confirmDelete.id);
      const outcome = result?.routingDeploy;
      // SASADM-0173 — the delete committed; a REJECTED / FAILED (SASADM-0176: or PARTIAL) deploy
      // shows the banner instead.
      if (isDeployProblem(outcome)) {
        setDeployFailure(outcome);
      } else if (result?.deletedSystemHostnames != null || result?.revokedInvitations != null) {
        snack.success(
          t(
            'products.apps.deletedCounts',
            'App deleted. Removed {{hosts}} platform hostname(s); revoked {{invites}} pending invitation(s).',
            {
              hosts: result.deletedSystemHostnames ?? 0,
              invites: result.revokedInvitations ?? 0,
            },
          ),
        );
      } else {
        snack.success(t('snackbar.appDeleted', 'App deleted.'));
      }
      setConfirmDelete(null);
      await refresh();
    } catch (err) {
      if (
        err instanceof ApiError &&
        (err.code === 'APP_HAS_HOSTNAMES' ||
          err.code === 'APP_IS_PRIMARY' ||
          err.code === 'PRIMARY_HOSTNAME_DELETE_BLOCKED' ||
          err.code === 'HOSTNAME_IS_REDIRECT_TARGET')
      ) {
        setDeleteRefusal({ err, app: confirmDelete });
      } else if (err instanceof ApiError) {
        snack.error(t(`errors.codes.${err.code}`, err.code));
      }
      setConfirmDelete(null);
    } finally {
      setBusy(false);
    }
  }

  // SASADM-0178 — the app losing the main address to `app`: the product's current primary.
  function previousPrimaryName(app: ProductAppDto): string | undefined {
    return rows?.find((r) => r.id !== app.id && r.isPrimary && roleCanBePrimary(r.role))
      ?.displayName;
  }

  // SASADM-0178 (SASATH-0395 §2) — a real set-primary's `apexPlan` is what moved; detected on the
  // response, so it is safe whatever the server version. Shown after the refresh (which clears it).
  function reportApexPlan(
    result: SetPrimaryResult,
    app: ProductAppDto,
    fromApp: string | undefined,
  ) {
    if (!result.apexPlan) return;
    setApexResult({ plan: result.apexPlan, appName: app.displayName, fromApp });
    setApexResultOpen(false);
  }

  // SASADM-0178 (SASATH-0395 §2) — with the read marker, Make / Re-apply primary dry-run first and
  // confirm with the plan. A failed dry run (e.g. 409 PRIMARY_APP_MUST_BE_PUBLIC) opens nothing.
  async function previewPrimary(app: ProductAppDto, reapply: boolean) {
    setBusy(true);
    setApexResult(null);
    try {
      const { apexPlan } = await apiAdmin.productApps.setPrimary(productId, app.id, {
        dryRun: true,
      });
      if (!apexPlan) {
        // Not a 0395 answer after all: the 0177 flow (the dry run wrote nothing).
        if (reapply) await reapplyPrimary(app);
        else setConfirmPrimary(app);
      } else if (reapply && apexPlan.movedCount === 0 && apexPlan.skippedCount === 0) {
        snack.info(
          t(
            'products.apps.reapplyNothing',
            "Nothing to repair — every tenant's main address is already on this app.",
          ),
        );
      } else {
        setPrimaryPreview({
          app,
          plan: apexPlan,
          reapply,
          fromApp: reapply ? undefined : previousPrimaryName(app),
        });
      }
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
    } finally {
      setBusy(false);
    }
  }

  async function changePrimary(app: ProductAppDto, makePrimary: boolean) {
    setBusy(true);
    setApexResult(null);
    const fromApp = makePrimary ? previousPrimaryName(app) : undefined;
    try {
      const updated: SetPrimaryResult = makePrimary
        ? await apiAdmin.productApps.setPrimary(productId, app.id)
        : await apiAdmin.productApps.clearPrimary(productId, app.id);
      // SASADM-0177 (SASATH-0394 §1) — Make primary moves every tenant's main address; a
      // REJECTED / FAILED / PARTIAL deploy is the banner, never a success toast.
      if (isDeployProblem(updated.routingDeploy)) {
        setDeployFailure(updated.routingDeploy);
      } else {
        snack.success(
          t(
            makePrimary ? 'products.apps.setPrimarySnack' : 'products.apps.clearPrimarySnack',
            makePrimary ? 'Primary app updated.' : 'Primary flag cleared.',
          ),
        );
      }
      await refresh();
      if (makePrimary) reportApexPlan(updated, app, fromApp);
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
    } finally {
      setBusy(false);
    }
  }

  // SASADM-0177 (SASATH-0394 §1) — repair on the app that is already primary: moves any tenant's
  // main address still on another app; `routingDeploy` is absent when there was nothing to fix.
  async function reapplyPrimary(app: ProductAppDto) {
    setBusy(true);
    setApexResult(null);
    try {
      const updated = await apiAdmin.productApps.setPrimary(productId, app.id);
      if (isDeployProblem(updated.routingDeploy)) {
        setDeployFailure(updated.routingDeploy);
      } else if (!updated.routingDeploy) {
        snack.info(
          t(
            'products.apps.reapplyNothing',
            "Nothing to repair — every tenant's main address is already on this app.",
          ),
        );
      } else {
        snack.success(
          t('products.apps.reapplyDone', "Repaired — tenants' main addresses moved to this app."),
        );
      }
      await refresh();
      reportApexPlan(updated, app, undefined);
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
    } finally {
      setBusy(false);
    }
  }

  function hostPattern(app: ProductAppDto): string {
    if (app.role === 'PUBLIC') {
      return app.isPrimary
        ? t('products.apps.roles.PUBLIC.hostPrimary', '<tenant>.<base>')
        : t('products.apps.roles.PUBLIC.host', '<app-slug>.<tenant>.<base>');
    }
    if (app.role === 'TENANT_ADMIN') {
      return t('products.apps.roles.TENANT_ADMIN.host', 'admin.<tenant>.<base>');
    }
    if (app.role === 'PRODUCT_ADMIN') {
      return t('products.apps.roles.PRODUCT_ADMIN.host', 'admin.{{slug}}.<base>', {
        slug: productSlug,
      });
    }
    if (app.role === 'SELF_CONTAINED') {
      // It anchors the tenant apex when primary — the product's single FQDN.
      return app.isPrimary
        ? t('products.apps.roles.SELF_CONTAINED.hostPrimary', '<tenant>.<base>')
        : t('products.apps.roles.SELF_CONTAINED.host', '<app-slug>.<tenant>.<base>');
    }
    if (app.role === 'AFFILIATE') {
      return t('products.apps.roles.AFFILIATE.host', 'affiliate.<tenant>.<base>');
    }
    return t('products.apps.roles.API.host', 'Reached at api.<each front-end host> (auto)');
  }

  const roleApps = appsByRole[appRole];
  const isPublic = appRole === 'PUBLIC';
  const isSelfContained = appRole === 'SELF_CONTAINED';
  // A filled singleton slot stays plainly hidden (cardinality, not
  // exclusivity); exclusivity instead greys the control and says why.
  const singletonFull = !isPublic && roleApps.length > 0;
  const exclusivityReason = singletonFull
    ? null
    : isSelfContained && hasAnyApp
      ? t(
          'products.apps.exclusivity.selfContainedNeedsEmpty',
          'A self-contained app must be the only app — remove the others first.',
        )
      : !isSelfContained && hasSelfContained
        ? t(
            'products.apps.exclusivity.blockedBySelfContained',
            "This product has a self-contained app, so it can't have other apps.",
          )
        : null;
  const topologyOk = topologyAllowsRole(appRole);
  // SASADM-0134 — only shown when the slot isn't already hidden for
  // cardinality reasons (singletonFull stays plainly hidden, unchanged).
  // Takes precedence over 0133's exclusivity reason when both apply,
  // since it is the harder, non-cardinality-based block.
  const topologyMismatchReason =
    singletonFull || topologyOk
      ? null
      : isSelfContained
        ? t(
            'products.apps.exclusivity.topologyMismatchComposed',
            'This product is composed — a self-contained app is not available. Change packaging first if you need one.',
          )
        : t(
            'products.apps.exclusivity.topologyMismatchSelfContained',
            'This product is self-contained — only its single self-contained app is allowed.',
          );
  const canAdd = topologyOk && !singletonFull && exclusivityReason === null;
  const blockedReason = topologyMismatchReason ?? exclusivityReason;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      {rows === null ? (
        <Typography color="text.secondary">{t('common.loading', 'Loading')}</Typography>
      ) : (
        <Stack spacing={2}>
          {isPublic && retagPromptOpen && publicApps.length > 0 && !tenantAdmin ? (
            <Alert
              severity="info"
              action={
                <IconButton
                  size="small"
                  aria-label={t('common.dismiss', 'Dismiss')}
                  onClick={() => setRetagPromptOpen(false)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {t(
                'products.apps.retagPrompt',
                "After the recent app-role update, existing apps are all 'Public'. If one of these is your per-tenant staff console, use Change role → Tenant-Admin.",
              )}
            </Alert>
          ) : null}

          {deployFailure && (
            <RoutingDeployBanner
              outcome={deployFailure}
              onClose={() => setDeployFailure(null)}
              action={
                // Product apps affect every tenant; the platform deploy lives on Operations.
                <Link component={RouterLink} to="/operations" color="inherit">
                  {t('routingDeploy.openOperations', 'Open Operations to deploy routing')}
                </Link>
              }
            />
          )}

          {apexResult && (
            // SASADM-0178 (SASATH-0395 §2) — what the real set-primary moved, beside the deploy outcome.
            <Alert
              severity="info"
              onClose={() => setApexResult(null)}
              closeText={t('common.close', 'Close')}
            >
              {t(
                'products.apps.apexPlan.result',
                'Primary app updated: {{moved}} moved, {{skipped}} skipped.',
                { moved: apexResult.plan.movedCount, skipped: apexResult.plan.skippedCount },
              )}
              <Box>
                <Button
                  size="small"
                  color="inherit"
                  aria-expanded={apexResultOpen}
                  onClick={() => setApexResultOpen((open) => !open)}
                >
                  {apexResultOpen
                    ? t('products.apps.apexPlan.hideDetails', 'Hide details')
                    : t('products.apps.apexPlan.showDetails', 'Show details')}
                </Button>
              </Box>
              <Collapse in={apexResultOpen} unmountOnExit>
                <ApexPlanView
                  plan={apexResult.plan}
                  appName={apexResult.appName}
                  fromApp={apexResult.fromApp}
                  mode="result"
                />
              </Collapse>
            </Alert>
          )}

          {deleteRefusal &&
            (deleteRefusal.err.code === 'HOSTNAME_IS_REDIRECT_TARGET' ? (
              // The source ids are cross-tenant here: the message only.
              <RedirectTargetNotice
                err={deleteRefusal.err}
                sourceHostnames={[]}
                onClose={() => setDeleteRefusal(null)}
              />
            ) : deleteRefusal.err.code === 'APP_IS_PRIMARY' ? (
              // SASADM-0177 — say what to do: clear the flag here, or move it to another app first.
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setDeleteRefusal(null)}
                closeText={t('common.close', 'Close')}
              >
                {deleteRefusal.err.details.message ??
                  t(`errors.codes.${deleteRefusal.err.code}`, deleteRefusal.err.code)}
                <Box sx={{ mt: 0.5 }}>
                  <Button
                    size="small"
                    color="inherit"
                    disabled={busy}
                    onClick={() => {
                      const { app } = deleteRefusal;
                      setDeleteRefusal(null);
                      void changePrimary(app, false);
                    }}
                  >
                    {t('products.apps.clearPrimary', 'Clear primary')}
                  </Button>
                  <Typography variant="caption" component="p">
                    {t(
                      'products.apps.isPrimaryHint',
                      'Or make another app primary first (Make primary on that app), then delete this one.',
                    )}
                  </Typography>
                </Box>
              </Alert>
            ) : (
              <TenantOverridesNotice
                err={deleteRefusal.err}
                onClose={() => setDeleteRefusal(null)}
              />
            ))}

          <Paper
            component="section"
            aria-labelledby={`app-role-${appRole}`}
            variant="outlined"
            sx={{ p: 2 }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-start' }, gap: 1 }}
            >
              <Box>
                <Typography id={`app-role-${appRole}`} variant="h4">
                  {t(`products.apps.roles.${appRole}.label`, appRole)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(`products.apps.roles.${appRole}.description`, '')}
                </Typography>
              </Box>
              {canAdd ? (
                <Button size="small" startIcon={<AddIcon />} onClick={() => openAdd(appRole)}>
                  {t(`products.apps.roles.${appRole}.add`, `Add ${appRole}`)}
                </Button>
              ) : blockedReason ? (
                <Box sx={{ textAlign: { sm: 'right' }, maxWidth: { sm: '18rem' } }}>
                  <Button size="small" startIcon={<AddIcon />} disabled>
                    {t(`products.apps.roles.${appRole}.add`, `Add ${appRole}`)}
                  </Button>
                  <Typography variant="caption" color="text.secondary" component="p">
                    {blockedReason}
                  </Typography>
                </Box>
              ) : null}
            </Stack>

            {roleApps.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                {t('products.apps.emptyRole', 'No app assigned to this role.')}
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {roleApps.map((app) => (
                  <AppRow
                    key={app.id}
                    app={app}
                    hostPattern={hostPattern(app)}
                    busy={busy}
                    tenantAdminExists={Boolean(tenantAdmin)}
                    affiliateExists={Boolean(affiliate)}
                    onEdit={() => openEdit(app)}
                    onDelete={() => requestDelete(app)}
                    onSetPrimary={() =>
                      // A PUBLIC app moves every tenant's main address: confirm first
                      // (SASADM-0178: with the dry run's plan when the server has it).
                      app.role !== 'PUBLIC'
                        ? void changePrimary(app, true)
                        : canPreview
                          ? void previewPrimary(app, false)
                          : setConfirmPrimary(app)
                    }
                    onClearPrimary={() => void changePrimary(app, false)}
                    onReapplyPrimary={() =>
                      canPreview ? void previewPrimary(app, true) : void reapplyPrimary(app)
                    }
                  />
                ))}
              </Stack>
            )}
          </Paper>
        </Stack>
      )}

      {dialogRole ? (
        <AppDialog
          open
          productId={productId}
          productSlug={productSlug}
          appRole={dialogRole}
          app={editApp}
          knownApps={rows ?? []}
          tenantAdminExists={Boolean(tenantAdmin)}
          affiliateExists={Boolean(affiliate)}
          willBecomePrimary={
            editApp === null &&
            roleCanBePrimary(dialogRole) &&
            !(rows?.some((app) => roleCanBePrimary(app.role)) ?? false)
          }
          onBlockedRoleChange={() =>
            snack.error(
              t(
                'errors.codes.APP_ROLE_CHANGE_BLOCKED',
                "Clear this app's Primary flag before changing its role.",
              ),
            )
          }
          onClose={closeEditor}
          onSaved={() => {
            closeEditor();
            void refresh();
          }}
          onDeployFailure={setDeployFailure}
          onStale={() => {
            void refresh();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(confirmPrimary)}
        title={t('products.apps.setPrimaryTitle', 'Make {{name}} the primary app?', {
          name: confirmPrimary?.displayName ?? '',
        })}
        body={t(
          'products.apps.setPrimaryBody',
          "Every tenant's main address will switch to this app. The current primary app keeps its own address on each tenant.",
        )}
        severity="warning"
        confirmLabel={t('products.apps.setPrimary', 'Make primary')}
        busy={busy}
        onConfirm={() => {
          const app = confirmPrimary;
          setConfirmPrimary(null);
          if (app) void changePrimary(app, true);
        }}
        onCancel={() => setConfirmPrimary(null)}
      />

      <ConfirmDialog
        open={Boolean(primaryPreview)}
        title={
          primaryPreview?.reapply
            ? t('products.apps.reapplyPrimaryTitle', 'Re-apply primary to {{name}}?', {
                name: primaryPreview.app.displayName,
              })
            : t('products.apps.setPrimaryTitle', 'Make {{name}} the primary app?', {
                name: primaryPreview?.app.displayName ?? '',
              })
        }
        body={
          primaryPreview ? (
            <ApexPlanView
              plan={primaryPreview.plan}
              appName={primaryPreview.app.displayName}
              fromApp={primaryPreview.fromApp}
              mode="preview"
            />
          ) : null
        }
        severity="warning"
        confirmLabel={
          primaryPreview?.reapply
            ? t('products.apps.reapplyPrimary', 'Re-apply primary')
            : t('products.apps.setPrimary', 'Make primary')
        }
        busy={busy}
        onConfirm={() => {
          const preview = primaryPreview;
          setPrimaryPreview(null);
          if (!preview) return;
          if (preview.reapply) void reapplyPrimary(preview.app);
          else void changePrimary(preview.app, true);
        }}
        onCancel={() => setPrimaryPreview(null)}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={t('products.apps.deleteTitle', 'Delete app?')}
        body={
          // SASADM-0176 (SASATH-0393 §1) — a tenant-scope delete takes its platform hostnames.
          confirmDelete && isTenantScoped(confirmDelete)
            ? t(
                'products.apps.deleteBody',
                "Delete {{name}}? This also removes the app's platform hostnames on every tenant and revokes pending invitations to it. This cannot be undone.",
                { name: confirmDelete.displayName },
              )
            : t('products.apps.deleteBodyProduct', 'Delete {{name}}? This cannot be undone.', {
                name: confirmDelete?.displayName ?? '',
              })
        }
        severity="error"
        confirmLabel={t('common.delete', 'Delete')}
        busy={busy}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
    </Paper>
  );
}

function AppRow({
  app,
  hostPattern,
  busy,
  tenantAdminExists,
  affiliateExists,
  onEdit,
  onDelete,
  onSetPrimary,
  onClearPrimary,
  onReapplyPrimary,
}: {
  app: ProductAppDto;
  hostPattern: string;
  busy: boolean;
  tenantAdminExists: boolean;
  affiliateExists: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onSetPrimary: () => void;
  onClearPrimary: () => void;
  onReapplyPrimary: () => void;
}) {
  const { t } = useCopy();
  const canChangePublicRole = roleChangeTargets(app, tenantAdminExists, affiliateExists).length > 0;

  return (
    <Box
      data-testid={`app-row-${app.id}`}
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        pt: 1.5,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(10rem, 1fr) minmax(12rem, 1.2fr) auto' },
        gap: 1.5,
        alignItems: 'center',
      }}
    >
      <Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
            {app.slug}
          </Typography>
          {roleCanBePrimary(app.role) && app.isPrimary ? (
            <Chip
              size="small"
              color="primary"
              icon={<StarIcon />}
              label={t('products.apps.primary', 'Primary')}
            />
          ) : null}
          {app.role === 'AFFILIATE' && app.tenantOptIn ? (
            <Chip size="small" label={t('products.apps.optIn.chip', 'Per-tenant opt-in')} />
          ) : null}
          {app.siteType === 'STATIC' ? (
            <Chip
              size="small"
              variant="outlined"
              label={t('products.apps.fields.siteTypeStaticChip', 'Static site')}
            />
          ) : null}
        </Stack>
        <Typography variant="body2">{app.displayName}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {roleUsesUpstream(app.role) ? app.upstream : app.docroot}
        </Typography>
        {app.cspFrameSrc ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontFamily: 'monospace' }}
          >
            {t('products.apps.fields.cspFrameSrc', 'Allowed iframe sources')}: {app.cspFrameSrc}
          </Typography>
        ) : null}
        {app.cspScriptSrc ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontFamily: 'monospace' }}
          >
            {t('products.apps.fields.cspScriptSrc', 'Allowed script sources')}: {app.cspScriptSrc}
          </Typography>
        ) : null}
        {app.cspConnectSrc ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontFamily: 'monospace' }}
          >
            {t('products.apps.fields.cspConnectSrc', 'Allowed connect sources')}:{' '}
            {app.cspConnectSrc}
          </Typography>
        ) : null}
        {app.cspFormAction ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontFamily: 'monospace' }}
          >
            {t('products.apps.fields.cspFormAction', 'Allowed form-action sources')}:{' '}
            {app.cspFormAction}
          </Typography>
        ) : null}
        {app.cspStyleSrc ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontFamily: 'monospace' }}
          >
            {t('products.apps.fields.cspStyleSrc', 'Allowed style sources')}: {app.cspStyleSrc}
          </Typography>
        ) : null}
        {app.cspFontSrc ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontFamily: 'monospace' }}
          >
            {t('products.apps.fields.cspFontSrc', 'Allowed font sources')}: {app.cspFontSrc}
          </Typography>
        ) : null}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {hostPattern}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {DERIVED_ROLE[app.role].kind} · {DERIVED_ROLE[app.role].scope}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.5} sx={{ justifyContent: { md: 'flex-end' } }}>
        {roleCanBePrimary(app.role) ? (
          app.isPrimary ? (
            <>
              <Tooltip
                title={t(
                  'products.apps.reapplyPrimaryHint',
                  "Moves any tenant's main address still left on another app to this one.",
                )}
              >
                <span>
                  <Button size="small" disabled={busy} onClick={onReapplyPrimary}>
                    {t('products.apps.reapplyPrimary', 'Re-apply primary')}
                  </Button>
                </span>
              </Tooltip>
              <Button size="small" disabled={busy} onClick={onClearPrimary}>
                {t('products.apps.clearPrimary', 'Clear primary')}
              </Button>
            </>
          ) : (
            <Button size="small" disabled={busy} onClick={onSetPrimary}>
              {t('products.apps.setPrimary', 'Make primary')}
            </Button>
          )
        ) : null}
        <IconButton
          aria-label={t('common.edit', 'Edit')}
          onClick={onEdit}
          disabled={busy}
          data-role-change-available={canChangePublicRole ? 'true' : undefined}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton aria-label={t('common.delete', 'Delete')} onClick={onDelete} disabled={busy}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

interface AppDialogProps {
  open: boolean;
  productId: number;
  productSlug: string;
  appRole: AppRole;
  app: ProductAppDto | null;
  /**
   * SASADM-0174 — the product's app list the caller already holds. In create mode, the SASATH-0390
   * fields show only when one of these reads carries their keys.
   */
  knownApps: ProductAppDto[];
  tenantAdminExists: boolean;
  /** SASADM-0172 — the product already has its AFFILIATE app (no PUBLIC → AFFILIATE re-tag). */
  affiliateExists?: boolean;
  onBlockedRoleChange: () => void;
  onClose: () => void;
  onSaved: () => void;
  /** SASADM-0152 — create-mode prefill (e.g. the reseller console's suggested slug/docroot). */
  defaults?: { slug: string; docroot: string };
  /**
   * SASADM-0152 — role-specific copy for backend codes, in place of `errors.codes.*`. A field
   * code still shows inline at its field; any other code listed here goes to the snackbar.
   */
  errorCopy?: Partial<Record<ErrorCode, string>>;
  /**
   * SASADM-0163 — true in create mode when this role is PUBLIC or SELF_CONTAINED and the
   * product has no such app yet. Recompute from the selected role if the create dialog
   * offers a role picker.
   */
  willBecomePrimary?: boolean;
  /** SASADM-0163 — parent re-fetches the app list (e.g. after a create PRIMARY_CONFLICT). */
  onStale?: () => void;
  /**
   * SASADM-0170 (SASATH-0387) — the save committed but its routing deploy was REJECTED / FAILED:
   * no success snackbar; the parent shows the banner. `onSaved` still runs. Without it, the
   * failure is reported as an error snackbar instead of the success one.
   */
  onDeployFailure?: (outcome: RoutingDeployOutcome) => void;
}

export function AppDialog({
  open,
  productId,
  productSlug,
  appRole,
  app,
  knownApps,
  tenantAdminExists,
  affiliateExists = false,
  onBlockedRoleChange,
  onClose,
  onSaved,
  defaults,
  errorCopy,
  willBecomePrimary = false,
  onStale,
  onDeployFailure,
}: AppDialogProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const isEdit = app !== null;
  const [selectedRole, setSelectedRole] = useState(appRole);
  const [slug, setSlug] = useState(app?.slug ?? defaults?.slug ?? '');
  const [displayName, setDisplayName] = useState(app?.displayName ?? '');
  const [docroot, setDocroot] = useState(app?.docroot ?? defaults?.docroot ?? '');
  const [upstream, setUpstream] = useState(app?.upstream ?? '');
  // SASADM-0172 — AFFILIATE per-tenant opt-in (default off) and FRONTEND iframe sources.
  const [tenantOptIn, setTenantOptIn] = useState(app?.tenantOptIn ?? false);
  const [cspFrameSrc, setCspFrameSrc] = useState(app?.cspFrameSrc ?? '');
  // SASADM-0173 (SASATH-0390 §2–§3) — FRONTEND site type and script / connect sources.
  const [siteType, setSiteType] = useState<SiteType>(app?.siteType ?? 'APP');
  const [cspScriptSrc, setCspScriptSrc] = useState(app?.cspScriptSrc ?? '');
  const [cspConnectSrc, setCspConnectSrc] = useState(app?.cspConnectSrc ?? '');
  // SASADM-0174 (SASATH-0390 §3, amended) — FRONTEND form-action sources.
  const [cspFormAction, setCspFormAction] = useState(app?.cspFormAction ?? '');
  // SASADM-0179 (SASATH-0397) — FRONTEND style / font sources.
  const [cspStyleSrc, setCspStyleSrc] = useState(app?.cspStyleSrc ?? '');
  const [cspFontSrc, setCspFontSrc] = useState(app?.cspFontSrc ?? '');
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState(false);
  const [renameConfirmOpen, setRenameConfirmOpen] = useState(false);
  const [optInOffConfirmOpen, setOptInOffConfirmOpen] = useState(false);
  const [roleConfirmOpen, setRoleConfirmOpen] = useState(false);
  const [targetRole, setTargetRole] = useState<AppRole | null>(null);

  const usesUpstream = roleUsesUpstream(selectedRole);
  const isAffiliate = selectedRole === 'AFFILIATE';
  // SASADM-0172 (SASATH-0385 §5) — FRONTEND roles carry a docroot and so iframe sources;
  // SASADM-0173 — and the site type and script / connect sources.
  const showsFrontendFields = DERIVED_ROLE[selectedRole].kind === 'Front-end';
  // SASADM-0174 — a SASATH-0390 field shows (and is sent) only when saas-auth's read carries its key:
  // the edited app's own read, or in create mode any app of the product.
  // SASADM-0179 — the SASATH-0397 keys detect the same way; needsNewerAuth stays 0390-only.
  const supports = (k: AppKey0390 | AppKey0397) => (app ? hasKey(app, k) : anyHasKey(knownApps, k));
  const needsNewerAuth = APP_KEYS_0390.every((k) => !supports(k));
  // SASADM-0163 — follow the selected role if create ever offers a picker.
  const becomePrimary = !isEdit && willBecomePrimary && roleCanBePrimary(selectedRole);
  const slugChanged = isEdit && slug.trim() !== app.slug;
  // SASADM-0172 — turning opt-in off gives every tenant an affiliate host; confirm first.
  const optInTurningOff = isEdit && isAffiliate && app.tenantOptIn === true && !tenantOptIn;
  const roleTargets = roleChangeTargets(app, tenantAdminExists, affiliateExists);
  const docrootError = error !== null && DOCROOT_ERROR_CODES.includes(error.code);
  const upstreamError = error !== null && UPSTREAM_ERROR_CODES.includes(error.code);
  const slugError = error !== null && SLUG_ERROR_CODES.includes(error.code);
  // SASADM-0173 (SASATH-0390 §3) — a 400 VALIDATION_FAILED message starts with the field it names;
  // it goes under that control, any other one to the banner.
  const validationMessage =
    error?.code === 'VALIDATION_FAILED' ? (error.details.message ?? '') : '';
  const cspFrameError = validationMessage.startsWith('cspFrameSrc');
  const cspScriptError = validationMessage.startsWith('cspScriptSrc');
  const cspConnectError = validationMessage.startsWith('cspConnectSrc');
  const cspFormActionError = validationMessage.startsWith('cspFormAction');
  const cspStyleError = validationMessage.startsWith('cspStyleSrc');
  const cspFontError = validationMessage.startsWith('cspFontSrc');
  const siteTypeError = validationMessage.startsWith('siteType');
  const fieldError =
    docrootError ||
    upstreamError ||
    slugError ||
    cspFrameError ||
    cspScriptError ||
    cspConnectError ||
    cspFormActionError ||
    cspStyleError ||
    cspFontError ||
    siteTypeError;

  function codeMessage(code: ErrorCode | undefined): string | undefined {
    if (!code) return undefined;
    return errorCopy?.[code] ?? t(`errors.codes.${code}`, code);
  }

  function requestRoleChange(target: AppRole) {
    if (!app) return;
    if (app.role === 'PUBLIC' && app.isPrimary) {
      onBlockedRoleChange();
      return;
    }
    setTargetRole(target);
    setRoleConfirmOpen(true);
  }

  // SASADM-0170 — true when the outcome is a failure (SASADM-0176: or PARTIAL), already reported:
  // skip the success snackbar.
  function reportedDeployFailure(outcome: RoutingDeployOutcome | undefined): boolean {
    if (!isDeployProblem(outcome)) return false;
    if (onDeployFailure) onDeployFailure(outcome);
    else if (isDeployPartial(outcome)) snack.warning(deployProblemText(outcome, t));
    else snack.error(deployProblemText(outcome, t));
    return true;
  }

  function onSubmit() {
    setError(null);
    if (optInTurningOff) {
      setOptInOffConfirmOpen(true);
      return;
    }
    submitAfterOptInConfirm();
  }

  function submitAfterOptInConfirm() {
    if (slugChanged && app.scope === 'TENANT') {
      setOptInOffConfirmOpen(false);
      setRenameConfirmOpen(true);
      return;
    }
    void doSave();
  }

  async function doSave() {
    if (pending) return;
    setError(null);
    setPending(true);
    // SASADM-0133 — docroot is never sent for a role that uses an upstream.
    // SASADM-0172 — tenantOptIn only for AFFILIATE; iframe sources only for FRONTEND roles,
    // trimmed. SASADM-0173 — likewise siteType and script / connect sources.
    // SASADM-0174 — and form-action sources; an unsupported SASATH-0390 key is never sent.
    // SASADM-0175 — send the trimmed string; "" clears. saas-auth treats null as absent (keep stored) on update.
    const roleFields = {
      ...(usesUpstream ? { upstream: upstream.trim() } : { docroot }),
      ...(isAffiliate ? { tenantOptIn } : {}),
      ...(showsFrontendFields
        ? {
            cspFrameSrc: cspFrameSrc.trim(),
            ...(supports('siteType') ? { siteType } : {}),
            ...(supports('cspScriptSrc') ? { cspScriptSrc: cspScriptSrc.trim() } : {}),
            ...(supports('cspConnectSrc') ? { cspConnectSrc: cspConnectSrc.trim() } : {}),
            ...(supports('cspFormAction') ? { cspFormAction: cspFormAction.trim() } : {}),
            // SASADM-0179 — the SASATH-0397 style / font sources, by the same rules.
            ...(supports('cspStyleSrc') ? { cspStyleSrc: cspStyleSrc.trim() } : {}),
            ...(supports('cspFontSrc') ? { cspFontSrc: cspFontSrc.trim() } : {}),
          }
        : {}),
    };
    try {
      if (isEdit) {
        const renameResult = slugChanged
          ? await apiAdmin.productApps.renameSlug(productId, app.id, slug.trim())
          : null;
        // SASADM-0172 — the rename carries its own routingDeploy (worst per-tenant outcome).
        const renameDeployFailed = reportedDeployFailure(renameResult?.routingDeploy);
        const updated = await apiAdmin.productApps.update(productId, app.id, {
          displayName,
          ...(selectedRole === app.role ? {} : { role: selectedRole }),
          ...roleFields,
        });
        if (reportedDeployFailure(updated.routingDeploy) || renameDeployFailed) {
          onSaved();
          return;
        }
        snack.success(
          renameResult
            ? app.scope === 'PRODUCT'
              ? t('products.apps.rename.snackProduct', 'App updated — slug renamed.')
              : t(
                  'products.apps.rename.snack',
                  'App updated — renamed {{hosts}} default hosts across {{tenants}} tenants.',
                  {
                    hosts: renameResult.rename.hostsRenamed,
                    tenants: renameResult.rename.tenantsAffected,
                  },
                )
            : t('snackbar.appUpdated', 'App updated.'),
        );
      } else {
        const created = await apiAdmin.productApps.create(productId, {
          slug: slug.trim(),
          role: selectedRole,
          displayName,
          ...roleFields,
        });
        // SASADM-0163 — pre-SASATH-0371 servers return isPrimary: false for a
        // first eligible app; call set-primary once. After 0371 this branch
        // does not run.
        if (becomePrimary && created.isPrimary === false) {
          try {
            await apiAdmin.productApps.setPrimary(productId, created.id);
          } catch {
            snack.error(
              t(
                'products.apps.primaryFallbackFailed',
                "App created, but it couldn't be made primary — use Make primary,",
              ),
            );
            onSaved();
            return;
          }
        }
        if (reportedDeployFailure(created.routingDeploy)) {
          onSaved();
          return;
        }
        snack.success(t('snackbar.appCreated', 'App created.'));
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) {
        // SASADM-0133 — exclusivity is a portfolio-level conflict, not a field
        // problem: the greyed-out Add controls are the guard, this is the
        // backstop, so it reads as a snackbar rather than an in-form error.
        const roleCopy = errorCopy?.[err.code];
        if (err.code === 'APP_SELF_CONTAINED_EXCLUSIVE') {
          snack.error(
            roleCopy ??
              t(
                'errors.codes.APP_SELF_CONTAINED_EXCLUSIVE',
                'A self-contained app must be the only app in its product.',
              ),
          );
        } else if (roleCopy !== undefined && !isFieldErrorCode(err.code)) {
          // SASADM-0152 — a caller-worded, non-field conflict (e.g. the reseller
          // console's singleton / Product Admin prerequisite) reads as a snackbar.
          snack.error(roleCopy);
        } else {
          setError(err);
          // SASADM-0163 — concurrent first-app create; refresh so willBecomePrimary
          // recomputes. Do not auto-resubmit.
          if (!isEdit && err.code === 'PRIMARY_CONFLICT') onStale?.();
        }
      }
    } finally {
      setRenameConfirmOpen(false);
      setOptInOffConfirmOpen(false);
      setPending(false);
    }
  }

  const hostHelper =
    selectedRole === 'PRODUCT_ADMIN'
      ? t('products.apps.roles.PRODUCT_ADMIN.host', 'admin.{{slug}}.<base>', {
          slug: productSlug,
        })
      : selectedRole === 'RESELLER_ADMIN'
        ? t('products.apps.roles.RESELLER_ADMIN.host', 'reseller.{{slug}}.<base>', {
            slug: productSlug,
          })
        : becomePrimary
          ? t(`products.apps.roles.${selectedRole}.hostPrimary`, '<tenant>.<base>')
          : t(`products.apps.roles.${selectedRole}.host`, '');
  const cspSourcesHelper = t(
    'products.apps.fields.cspSourcesHelper',
    "Space-separated https:// origins or https://*.domain, at most 12. Third-party scripts or APIs this app's pages may use (e.g. analytics, chat widgets). Only add sources you trust; they run inside this app's pages.",
  );

  return (
    <>
      <Dialog open={open} onClose={pending ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEdit
            ? t('products.apps.edit', 'Edit app')
            : t(`products.apps.roles.${appRole}.add`, 'Add app')}
        </DialogTitle>
        <DialogContent>
          {/* SASADM-0171 (SASATH-0389 §4.1) — a role change refused for tenant overrides;
              SASADM-0172 (SASATH-0388 §3C.2) — a docroot used by a tenant's custom build. */}
          {error?.code === 'APP_HAS_TENANT_OVERRIDES' ||
          error?.code === 'DOCROOT_IN_USE_BY_TENANT_OVERRIDE' ? (
            <TenantOverridesNotice err={error} />
          ) : (
            <ErrorBanner
              error={fieldError ? null : error}
              message={
                !isEdit && error?.code === 'PRIMARY_CONFLICT'
                  ? t(
                      'products.apps.primaryConflict',
                      'Another app was just added to this product. The list has been refreshed — review and save again.',
                    )
                  : null
              }
            />
          )}
          <Box
            component="form"
            id="app-form"
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
            noValidate
          >
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('products.apps.fields.role', 'Role')}
                </Typography>
                <Typography variant="body2">
                  {t(`products.apps.roles.${selectedRole}.label`, selectedRole)} —{' '}
                  {DERIVED_ROLE[selectedRole].kind},{' '}
                  {DERIVED_ROLE[selectedRole].scope.toLowerCase()}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: 'monospace' }}
                >
                  {hostHelper}
                </Typography>
                {becomePrimary ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t(
                      `products.apps.firstAppPrimary.${selectedRole}`,
                      selectedRole === 'SELF_CONTAINED'
                        ? "This is the product's first self-contained app, so it becomes the primary app."
                        : "This is the product's first public app, so it becomes the primary app.",
                    )}
                  </Typography>
                ) : null}
                {isEdit && roleTargets.length > 0 ? (
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                    {roleTargets.map((target) => (
                      <Button
                        key={target}
                        size="small"
                        onClick={() => requestRoleChange(target)}
                        disabled={pending}
                      >
                        {t('products.apps.changeRole.action', 'Change role')} →{' '}
                        {t(`products.apps.roles.${target}.label`, target)}
                      </Button>
                    ))}
                  </Stack>
                ) : null}
              </Box>
              <TextField
                fullWidth
                required
                label={t('products.apps.fields.slug', 'Slug')}
                value={slug}
                onChange={(event) => setSlug(event.target.value.toLowerCase())}
                error={slugError}
                helperText={
                  slugError
                    ? codeMessage(error?.code)
                    : isEdit
                      ? t(
                          'products.apps.fields.slugRenameHelper',
                          'Renaming changes default URLs; custom domains keep their name.',
                        )
                      : t(
                          'products.apps.fields.slugHelper',
                          'Lowercase letters, numbers, hyphens. Unique within the product.',
                        )
                }
              />
              <TextField
                fullWidth
                required
                label={t('products.apps.fields.displayName', 'Display name')}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
              {usesUpstream ? (
                <TextField
                  fullWidth
                  required
                  label={t('products.apps.fields.upstream', 'Upstream')}
                  value={upstream}
                  onChange={(event) => setUpstream(event.target.value)}
                  error={upstreamError}
                  helperText={
                    upstreamError
                      ? codeMessage(error?.code)
                      : t('products.apps.fields.upstreamHelper', '')
                  }
                />
              ) : (
                <TextField
                  fullWidth
                  required
                  label={t('products.apps.fields.docroot', 'Docroot')}
                  value={docroot}
                  onChange={(event) => setDocroot(event.target.value)}
                  error={docrootError}
                  helperText={
                    docrootError
                      ? codeMessage(error?.code)
                      : t('products.apps.fields.docrootHelper', '')
                  }
                />
              )}
              {showsFrontendFields ? (
                <>
                  <TextField
                    fullWidth
                    label={t('products.apps.fields.cspFrameSrc', 'Allowed iframe sources')}
                    value={cspFrameSrc}
                    onChange={(event) => setCspFrameSrc(event.target.value)}
                    error={cspFrameError}
                    helperText={
                      cspFrameError
                        ? validationMessage
                        : t(
                            'products.apps.fields.cspFrameSrcHelper',
                            'Space-separated. blob: (in-page file previews), https:// origins and/or https://*.domain this app may show in an iframe, e.g. blob: https://aem.cnppowerforms.com. At most 8.',
                          )
                    }
                    slotProps={{ htmlInput: { sx: { fontFamily: 'monospace' } } }}
                  />
                  {needsNewerAuth ? (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {t(
                        'products.apps.fields.needsNewerAuth',
                        'Site type and extra CSP sources appear once saas-auth is updated.',
                      )}
                    </Typography>
                  ) : null}
                  {supports('siteType') ? (
                    <FormControl component="fieldset" error={siteTypeError}>
                      <FormLabel component="legend">
                        {t('products.apps.fields.siteType', 'Site type')}
                      </FormLabel>
                      <RadioGroup
                        row
                        value={siteType}
                        onChange={(event) => setSiteType(event.target.value as SiteType)}
                      >
                        <FormControlLabel
                          value="APP"
                          control={<Radio />}
                          label={t(
                            'products.apps.fields.siteTypeApp',
                            'Application: every unknown path loads the app',
                          )}
                        />
                        <FormControlLabel
                          value="STATIC"
                          control={<Radio />}
                          label={t(
                            'products.apps.fields.siteTypeStatic',
                            'Static site: real pages, and a real 404 that serves /404.html',
                          )}
                        />
                      </RadioGroup>
                      {siteTypeError ? <FormHelperText>{validationMessage}</FormHelperText> : null}
                    </FormControl>
                  ) : null}
                  {supports('cspScriptSrc') ? (
                    <TextField
                      fullWidth
                      label={t('products.apps.fields.cspScriptSrc', 'Allowed script sources')}
                      value={cspScriptSrc}
                      onChange={(event) => setCspScriptSrc(event.target.value)}
                      error={cspScriptError}
                      helperText={cspScriptError ? validationMessage : cspSourcesHelper}
                      slotProps={{ htmlInput: { sx: { fontFamily: 'monospace' } } }}
                    />
                  ) : null}
                  {supports('cspConnectSrc') ? (
                    <TextField
                      fullWidth
                      label={t('products.apps.fields.cspConnectSrc', 'Allowed connect sources')}
                      value={cspConnectSrc}
                      onChange={(event) => setCspConnectSrc(event.target.value)}
                      error={cspConnectError}
                      helperText={cspConnectError ? validationMessage : cspSourcesHelper}
                      slotProps={{ htmlInput: { sx: { fontFamily: 'monospace' } } }}
                    />
                  ) : null}
                  {supports('cspFormAction') ? (
                    <TextField
                      fullWidth
                      label={t('products.apps.fields.cspFormAction', 'Allowed form-action sources')}
                      value={cspFormAction}
                      onChange={(event) => setCspFormAction(event.target.value)}
                      error={cspFormActionError}
                      helperText={
                        cspFormActionError
                          ? validationMessage
                          : t(
                              'products.apps.fields.cspFormActionHelper',
                              "Other sites this app's forms may submit to (e.g. https://api.themoneymeeting.ai). Space-separated https:// origins or https://*.domain, at most 12.",
                            )
                      }
                      slotProps={{ htmlInput: { sx: { fontFamily: 'monospace' } } }}
                    />
                  ) : null}
                  {supports('cspStyleSrc') ? (
                    <TextField
                      fullWidth
                      label={t('products.apps.fields.cspStyleSrc', 'Allowed style sources')}
                      value={cspStyleSrc}
                      onChange={(event) => setCspStyleSrc(event.target.value)}
                      error={cspStyleError}
                      helperText={
                        cspStyleError
                          ? validationMessage
                          : t(
                              'products.apps.fields.cspStyleSrcHelper',
                              "Stylesheet origins added to this app's style-src.",
                            )
                      }
                      slotProps={{ htmlInput: { sx: { fontFamily: 'monospace' } } }}
                    />
                  ) : null}
                  {supports('cspFontSrc') ? (
                    <TextField
                      fullWidth
                      label={t('products.apps.fields.cspFontSrc', 'Allowed font sources')}
                      value={cspFontSrc}
                      onChange={(event) => setCspFontSrc(event.target.value)}
                      error={cspFontError}
                      helperText={
                        cspFontError
                          ? validationMessage
                          : t(
                              'products.apps.fields.cspFontSrcHelper',
                              "Web-font origins added to this app's font-src.",
                            )
                      }
                      slotProps={{ htmlInput: { sx: { fontFamily: 'monospace' } } }}
                    />
                  ) : null}
                </>
              ) : null}
              {isAffiliate ? (
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={tenantOptIn}
                        onChange={(event) => setTenantOptIn(event.target.checked)}
                        disabled={pending}
                      />
                    }
                    label={t('products.apps.optIn.label', 'Per-tenant opt-in')}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', ml: 6 }}
                  >
                    {t(
                      'products.apps.optIn.help',
                      "When on, tenants get this app's affiliate host only where you enable it (Tenant → App builds). When off, every tenant of the product gets one.",
                    )}
                  </Typography>
                </Box>
              ) : null}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={pending}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button type="submit" form="app-form" variant="contained" disabled={pending}>
            {t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={roleConfirmOpen}
        title={t('products.apps.changeRole.title', 'Change app role?')}
        body={t('products.apps.changeRole.body', 'Change {{name}} from {{from}} to {{to}}?', {
          name: app?.displayName ?? '',
          from: app ? t(`products.apps.roles.${app.role}.label`, app.role) : '',
          to: targetRole ? t(`products.apps.roles.${targetRole}.label`, targetRole) : '',
        })}
        confirmLabel={t('products.apps.changeRole.confirm', 'Change role')}
        onConfirm={() => {
          if (targetRole) setSelectedRole(targetRole);
          setRoleConfirmOpen(false);
        }}
        onCancel={() => setRoleConfirmOpen(false)}
      />

      <ConfirmDialog
        open={renameConfirmOpen}
        title={t('products.apps.rename.confirmTitle', 'Rename this app?')}
        body={t(
          'products.apps.rename.confirmBody',
          'Renaming “{{old}}” to “{{new}}” changes the default hostname on every tenant of this product and reloads routing. Custom domains for this app are not renamed.',
          { old: app?.slug ?? '', new: slug.trim() },
        )}
        confirmLabel={t('products.apps.rename.confirmLabel', 'Rename')}
        busy={pending}
        onConfirm={() => void doSave()}
        onCancel={() => setRenameConfirmOpen(false)}
      />

      <ConfirmDialog
        open={optInOffConfirmOpen}
        title={t('products.apps.optIn.offTitle', 'Give every tenant an affiliate host?')}
        body={t(
          'products.apps.optIn.offBody',
          'Turning per-tenant opt-in off creates affiliate.<tenant>.<base> for every tenant of this product and deploys routing.',
        )}
        severity="warning"
        confirmLabel={t('products.apps.optIn.offConfirm', 'Turn opt-in off')}
        busy={pending}
        onConfirm={submitAfterOptInConfirm}
        onCancel={() => setOptInOffConfirmOpen(false)}
      />
    </>
  );
}
